import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import { useAppDispatch, useAppSelector } from "../store";
import {
  fetchVideoById,
  selectCurrentVideo,
  selectCurrentVideoLoading,
  selectCurrentVideoError,
  clearCurrentVideo,
} from "../store/content/slice";
import { ShortsPlayer } from "../components/shorts/ShortsPlayer";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBoundary from "../components/common/ErrorBoundary/ErrorBoundary";
import { ErrorDisplay } from "../components/common/ErrorBoundary";
import { useTheme } from "../contexts/ThemeContext";
import { useDirection } from "../contexts/DirectionContext";
import { ContentService } from "../services/content";
import { CONTENT_TYPES, CONTENT_SORT_VALUES, DURATION_RANGES } from "../constants/content";
import { VideoContent } from "../types/content";
import { SharePopup } from '../components/common/SharePopup';
import { useVideoActions } from '../hooks/useVideoActions';
import { useBranding } from "../contexts/BrandingContext";
import BottomSheet from '../components/common/BottomSheet';
import VideoDetails from '../components/video/VideoDetails';
import SEO from "../components/common/SEO";

const BATCH_SIZE = 10;
const PREFETCH_THRESHOLD = 3;

const Shorts: React.FC = () => {
  const { shortId } = useParams<{ shortId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const { branding } = useBranding();
  
  // UI State
  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  // Content State
  const [shorts, setShorts] = useState<VideoContent[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);

  const currentVideo = useAppSelector(selectCurrentVideo);
  const isLoading = useAppSelector(selectCurrentVideoLoading);
  const error = useAppSelector(selectCurrentVideoError);

  // Get the currently active video
  const activeVideo = shorts[currentIndex];

  const {
    isSaved: videoActionsSaved,
    isSharePopupOpen: videoActionsSharePopupOpen,
    shareOptions: videoActionsShareOptions,
    handleShare: videoActionsHandleShare,
    handleSave: videoActionsHandleSave,
    closeSharePopup: videoActionsCloseSharePopup,
  } = useVideoActions({
    video: activeVideo,
  });

  // Helper function to deduplicate shorts
  const deduplicateShorts = useCallback((newShorts: VideoContent[], existingShorts: VideoContent[] = []): VideoContent[] => {
    const seen = new Set(existingShorts.map(s => s.id));
    return newShorts.filter(short => {
      if (seen.has(short.id)) return false;
      seen.add(short.id);
      return true;
    });
  }, []);

  // Fetch more shorts when needed
  const fetchMoreShorts = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const service = ContentService.getInstance();
      const searchParams = {
        type: CONTENT_TYPES.VIDEO,
        duration: DURATION_RANGES.SHORTS,
        sortBy: CONTENT_SORT_VALUES.CREATED_AT,
        sortType: "desc" as const,
        limit: BATCH_SIZE,
        page: currentPage,
      };

      const response = await service.searchContent(searchParams);
      const filteredResults = response.results.filter(
        (item): item is VideoContent => item.type === CONTENT_TYPES.VIDEO
      );

      if (!filteredResults.length) {
        setHasMore(false);
        if (shorts.length === 0) {
          setInitializationError("No shorts available");
        }
        return;
      }

      const uniqueResults = deduplicateShorts(filteredResults, shorts);
      if (uniqueResults.length === 0) {
        setHasMore(false);
      } else {
        setShorts(prev => [...prev, ...uniqueResults]);
        setCurrentPage(prev => prev + 1);
      }
    } catch (error) {
      console.error("Failed to fetch more shorts:", error);
      if (shorts.length === 0) {
        setInitializationError("Failed to load shorts");
      }
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, isLoadingMore, hasMore, deduplicateShorts, shorts]);

  // Reset state for new session
  const resetState = useCallback(() => {
    setCurrentPage(1);
    setHasMore(true);
    setShorts([]);
    setIsInitialized(false);
    setInitializationError(null);
  }, []);

  // Initialize shorts - either with specific ID or from the beginning
  useEffect(() => {
    const initializeShorts = async () => {
      if (isInitialized) return;
      
      resetState();

      if (shortId) {
        try {
          await dispatch(fetchVideoById(shortId)).unwrap();
        } catch (error) {
          console.error("Failed to fetch initial video:", error);
          setInitializationError("Failed to load the requested video");
          // Continue to fetch other shorts even if the specific video fails
          fetchMoreShorts();
        }
      } else {
        fetchMoreShorts();
      }
      setIsInitialized(true);
    };

    initializeShorts();
    return () => {
      dispatch(clearCurrentVideo());
    };
  }, [shortId, dispatch, fetchMoreShorts, isInitialized, resetState]);

  // Handle the case when we have a shortId and the video is loaded
  useEffect(() => {
    if (shortId && currentVideo && !shorts.includes(currentVideo)) {
      // Insert the current video at the beginning and maintain uniqueness
      const updatedShorts = deduplicateShorts([currentVideo, ...shorts]);
      setShorts(updatedShorts);
      setCurrentIndex(0);
      
      // Only fetch more if we don't have enough shorts
      if (updatedShorts.length < BATCH_SIZE) {
        fetchMoreShorts();
      }
    }
  }, [currentVideo, shortId, shorts, deduplicateShorts, fetchMoreShorts]);

  // Check if we need to fetch more videos - trigger earlier
  useEffect(() => {
    if (currentIndex >= shorts.length - PREFETCH_THRESHOLD && hasMore && !isLoadingMore) {
      fetchMoreShorts();
    }
  }, [currentIndex, shorts.length, hasMore, fetchMoreShorts, isLoadingMore]);

  // Navigation function
  const navigateToShort = useCallback((index: number) => {
    if (index >= 0 && index < shorts.length) {
      setCurrentIndex(index);
      navigate(`/shorts/${shorts[index].id}`, { replace: true });
    }
  }, [shorts, navigate]);

  // Share handler
  const handleShareClick = useCallback(() => {
    if (!activeVideo) return;
    videoActionsHandleShare();
  }, [activeVideo, videoActionsHandleShare]);

  // Save handler
  const handleSaveClick = useCallback(() => {
    videoActionsHandleSave();
  }, [videoActionsHandleSave]);

  // Handle scroll/swipe events
  const handleNext = useCallback(() => {
    if (currentIndex >= shorts.length - 1) return;
    const container = containerRef.current;
    if (container) {
      const nextPosition = (currentIndex + 1) * container.clientHeight;
      container.scrollTo({
        top: nextPosition,
        behavior: 'smooth'
      });
    }
  }, [currentIndex, shorts.length]);

  const handlePrevious = useCallback(() => {
    if (currentIndex <= 0) return;
    const container = containerRef.current;
    if (container) {
      const prevPosition = (currentIndex - 1) * container.clientHeight;
      container.scrollTo({
        top: prevPosition,
        behavior: 'smooth'
      });
    }
  }, [currentIndex]);

  // Add container ref
  const containerRef = useRef<HTMLDivElement>(null);

  // Toggle description panel
  const toggleDescription = useCallback(() => {
    setIsDescriptionOpen(prev => !prev);
  }, []);

  // Handle scroll events
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollPosition = container.scrollTop;
      const viewportHeight = container.clientHeight;
      const newIndex = Math.round(scrollPosition / viewportHeight);
      
      // Only update if the index has changed
      if (newIndex !== currentIndex && newIndex < shorts.length) {
        setCurrentIndex(newIndex);
        navigate(`/shorts/${shorts[newIndex].id}`, { replace: true });
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [currentIndex, shorts, navigate]);

  // Sync scroll position when currentIndex changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const targetPosition = currentIndex * container.clientHeight;
    container.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
  }, [currentIndex]);

  // Add new state for video playback
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <SEO title={t('video.loading')} noindex={true} />
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if ((error || initializationError) && !shorts.length) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-4">
        <SEO title={t('errors.somethingWentWrong')} noindex={true} />
        <ErrorDisplay
          error={error || initializationError}
          onRetry={() => {
            resetState();
            if (shortId) {
              dispatch(fetchVideoById(shortId));
            } else {
              fetchMoreShorts();
            }
          }}
          className="mb-4"
          compact
        />
        <button
          onClick={() => navigate("/")}
          className="text-primary-500 hover:text-primary-600 transition-colors"
        >
          {t("common.backToHome")}
        </button>
      </div>
    );
  }

  // No content state
  if (!activeVideo && shorts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-4 text-text-primary">
        <SEO 
          title={t('en:content.trendingShorts')}
          description={t('en:search.format.shorts')}
          noindex={true}
        />
        <Icon icon="ph:video-camera-slash-thin" className="w-16 h-16 mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t("en:video.notFound")}</h2>
        <h2 className="text-xl font-semibold mb-2">{t("video.notFound")}</h2>
        <p className="text-text-secondary text-center">
          {t("video.notFoundDescription")}
        </p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 text-primary-500 hover:text-primary-600 transition-colors"
        >
          {t("common.backToHome")}
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className={`min-h-screen bg-background-primary ${isRTL ? "rtl" : "ltr"}`}>
        <SEO
          title={activeVideo.title}
          description={activeVideo.description}
          image={activeVideo.thumbnail}
          keywords={[
            ...(activeVideo.topics || []),
            'Jewish shorts',
            'Torah shorts',
            'Jewish education',
            'Quick lessons'
          ]}
        />
        <div 
          ref={containerRef} 
          className="fixed inset-0 bg-black overflow-y-scroll snap-y snap-mandatory scrollbar-none"
        >
          <div className="relative">
            {/* Navigation Arrows - Desktop Only */}
            <div className={`hidden lg:block fixed ${isRTL ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 z-10`}>
              <div className="flex flex-col gap-2">
                <button 
                  onClick={handlePrevious}
                  className={`p-2 rounded-full backdrop-blur-sm transition-colors
                    ${currentIndex > 0 
                      ? 'bg-black/40 hover:bg-black/60 cursor-pointer' 
                      : 'bg-black/20 cursor-not-allowed opacity-50'
                    }`}
                  disabled={currentIndex === 0}
                  aria-label={t("common.previous")}
                >
                  <Icon icon="ph:caret-up-bold" className="w-6 h-6 text-white" />
                </button>
                <button 
                  onClick={handleNext}
                  className={`p-2 rounded-full backdrop-blur-sm transition-colors
                    ${currentIndex < shorts.length - 1 
                      ? 'bg-black/40 hover:bg-black/60 cursor-pointer' 
                      : 'bg-black/20 cursor-not-allowed opacity-50'
                    }`}
                  disabled={currentIndex >= shorts.length - 1 || isLoadingMore}
                  aria-label={t("common.next")}
                >
                  <Icon icon="ph:caret-up-bold" className="w-6 h-6 text-white rotate-180" />
                </button>
              </div>
            </div>

            {/* Shorts List */}
            {shorts.map((short, index) => (
              <div 
                key={short.id} 
                className="snap-start h-screen w-full flex items-center justify-center"
              >
                <div className={`
                  relative flex items-center
                  transition-transform duration-300
                  ${isDescriptionOpen ? isRTL ? 'lg:translate-x-[200px]' : 'lg:translate-x-[-200px]' : ''}
                `}>
                  {/* Back Button - Desktop */}
                  <div className={`hidden lg:flex ${isRTL ? 'ml-4' : 'mr-4'} self-start mt-4`}>
                    <button 
                      onClick={() => navigate("/")}
                      className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors"
                    >
                      <Icon 
                        icon={isRTL ? "ph:arrow-right-thin" : "ph:arrow-left-thin"} 
                        className="w-6 h-6 text-white" 
                      />
                    </button>
                  </div>

                  {/* Shorts Container */}
                  <div 
                    className={`
                      relative w-screen h-screen 
                      lg:w-auto lg:h-[calc(100vh-4rem)] lg:aspect-[9/16]
                      lg:rounded-xl lg:overflow-hidden
                    `}
                  >
                    {/* Shorts Player */}
                    <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
                      <ShortsPlayer
                        video={short}
                        className="h-full w-full lg:rounded-xl"
                        onPlayStateChange={(playing) => {
                          if (index === currentIndex) {
                            setIsVideoPlaying(playing);
                          }
                        }}
                        autoPlay={index === currentIndex}
                      />
                    </div>

                    {/* Loading indicator for more shorts */}
                    {isLoadingMore && index === shorts.length - 1 && (
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                        <LoadingSpinner size="sm" />
                      </div>
                    )}

                    {/* Overlay Controls */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="relative w-full h-full">
                        {/* Top Bar - Mobile Only */}
                        <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-center pointer-events-auto z-10 lg:hidden">
                          <button
                            onClick={() => navigate("/")}
                            className="text-white hover:text-gray-200 transition-colors rounded-full p-2 bg-black/40 hover:bg-black/60 backdrop-blur-sm"
                          >
                            <Icon 
                              icon={isRTL ? "ph:arrow-right-thin" : "ph:arrow-left-thin"} 
                              className="w-6 h-6" 
                            />
                          </button>
                        </div>

                        {/* Bottom Info */}
                        <div className="absolute bottom-0 left-0 right-0 pointer-events-auto">
                          {/* Dark Gradient Overlay */}
                          <div className="absolute bottom-0 left-0 right-0 h-[200px] bg-gradient-to-t from-black/80 via-black/50 to-transparent pointer-events-none" />
                          
                          <div className={`relative p-4 ${isRTL ? 'pl-20' : 'pr-20'} lg:px-4`}>
                            <div 
                              className="space-y-2 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDescription();
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  toggleDescription();
                                }
                              }}
                            >
                              <h2 className="text-white text-lg font-semibold line-clamp-2">
                                {short.title}
                              </h2>
                              {short.description && (
                                <p className="text-white/80 text-sm line-clamp-2">
                                  {short.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons - Mobile Only */}
                        <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} bottom-0 flex flex-col gap-3 pointer-events-auto lg:hidden p-4 z-10`}>
                          {/* Share Button */}
                          <button 
                            onClick={handleShareClick}
                            className="group flex flex-col items-center"
                          >
                            <div className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center group-hover:bg-black/60 transition-colors">
                              <Icon icon="ph:share-thin" className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-white text-sm font-medium mt-1">{t("common.share")}</span>
                          </button>

                          {/* Save Button */}
                          <button 
                            onClick={handleSaveClick}
                            className="group flex flex-col items-center"
                          >
                            <div className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center group-hover:bg-black/60 transition-colors">
                              <Icon icon={videoActionsSaved ? "ph:bookmark-simple-fill" : "ph:bookmark-simple-thin"} className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-white text-sm font-medium mt-1">{t("common.save")}</span>
                          </button>

                          {/* Description Button */}
                          <button 
                            onClick={toggleDescription}
                            className="group flex flex-col items-center"
                          >
                            <div className={`
                              w-12 h-12 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors
                              ${isDescriptionOpen ? 'bg-white/20 hover:bg-white/30' : 'bg-black/40 hover:bg-black/60'}
                            `}>
                              <Icon icon="ph:info-thin" className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-white text-sm font-medium mt-1">{t("common.details")}</span>
                          </button>

                          {/* Logo */}
                          <div className="group flex flex-col items-center">
                            <img
                              src={theme.mode === "dark" ? branding.logo.dark || branding.logo.light : branding.logo.light}
                              alt={t("common.creator")}
                              className="w-12 h-12 rounded-full border-2 border-border-primary"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons - Desktop Only */}
                <div className={`hidden lg:flex flex-col gap-6 ${isRTL ? 'mr-4' : 'ml-4'}`}>
                  {/* Share Button */}
                  <button 
                    onClick={handleShareClick}
                    className="group flex flex-col items-center"
                  >
                    <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/20 transition-colors">
                      <Icon icon="ph:share-thin" className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-white text-sm font-medium mt-1">{t("common.share")}</span>
                  </button>

                  {/* Save Button */}
                  <button 
                    onClick={handleSaveClick}
                    className="group flex flex-col items-center"
                  >
                    <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/20 transition-colors">
                      <Icon icon={videoActionsSaved ? "ph:bookmark-simple-fill" : "ph:bookmark-simple-thin"} className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-white text-sm font-medium mt-1">{t("common.save")}</span>
                  </button>

                  {/* Description Button */}
                  <button 
                    onClick={toggleDescription}
                    className="group flex flex-col items-center"
                  >
                    <div className={`
                      w-12 h-12 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors
                      ${isDescriptionOpen ? 'bg-white/30 hover:bg-white/40' : 'bg-white/10 hover:bg-white/20'}
                    `}>
                      <Icon icon="ph:info-thin" className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-white text-sm font-medium mt-1">{t("common.details")}</span>
                  </button>

                  {/* Logo */}
                  <div className="group flex flex-col items-center">
                    <img
                      src={theme.mode === "dark" ? branding.logo.dark || branding.logo.light : branding.logo.light}
                      alt={t("common.creator")}
                      className="w-12 h-12 rounded-full border-2 border-border-primary"
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Description Panel - Mobile */}
            <BottomSheet
              isOpen={isDescriptionOpen}
              onClose={() => setIsDescriptionOpen(false)}
              title={t("common.details")}
            >
              {activeVideo && (
                <VideoDetails
                  video={activeVideo}
                  layout="shorts"
                  onShare={handleShareClick}
                  onSave={handleSaveClick}
                  isSaved={videoActionsSaved}
                />
              )}
            </BottomSheet>

            {/* Description Panel - Desktop */}
            <div className={`
              hidden lg:block fixed ${isRTL ? 'left-0' : 'right-0'} top-0 h-full w-[400px] bg-surface-primary
              transform transition-transform duration-300 ease-in-out
              ${isDescriptionOpen ? 'translate-x-0' : isRTL ? '-translate-x-full' : 'translate-x-full'}
              overflow-y-auto
              scrollbar-thin scrollbar-track-surface-secondary scrollbar-thumb-border-primary
              hover:scrollbar-thumb-primary-500/50
            `}>
              <div className="p-6">
                {/* Close Button */}
                <button
                  onClick={toggleDescription}
                  className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} p-2 rounded-full hover:bg-surface-secondary`}
                >
                  <Icon icon="ph:x-thin" className="w-6 h-6 text-text-primary" />
                </button>

                {/* Video Details */}
                <div className="mt-8">
                  {activeVideo && (
                    <VideoDetails
                      video={activeVideo}
                      layout="shorts"
                      onShare={handleShareClick}
                      onSave={handleSaveClick}
                      isSaved={videoActionsSaved}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <SharePopup
          isOpen={videoActionsSharePopupOpen}
          onClose={videoActionsCloseSharePopup}
          title={activeVideo?.title}
          url={window.location.href}
          shareOptions={videoActionsShareOptions}
        />
      </div>
    </ErrorBoundary>
  );
};

export default Shorts; 